import React from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { BookOpenIcon } from "@heroicons/react/24/outline";
import DashboardLayout from "../components/DashboardLayout";
import ModalRoute from "../components/common/ModalRoute";
import ApiAccessEmptyState from "../components/api/ApiAccessEmptyState";
import ApiPlayground from "../components/api/ApiPlayground";
import ApiKeys from "../components/api/ApiKeys";
import ApiUsage from "../components/api/ApiUsage";
import ApiRequestDetail from "../components/api/ApiRequestDetail";
import AgentInstructionsModal from "../components/api/AgentInstructionsModal";
import { RobotHeadIcon } from "../components/api/shared";
import { getExternalApiBaseUrl } from "../services/externalApi";

const PLAN_HAS_API = new Set(["launch", "builder", "factory", "enterprise"]);

const Api = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const planKey = (currentUser?.limits?.key || "blueprint").toLowerCase();
  const hasApiAccess = PLAN_HAS_API.has(planKey);

  if (!hasApiAccess) {
    return (
      <DashboardLayout title="API" currentUser={currentUser}>
        <ApiAccessEmptyState />
      </DashboardLayout>
    );
  }

  const path = location.pathname;
  const activeTab = path.includes("/usage")
    ? "usage"
    : path.includes("/keys")
      ? "keys"
      : "playground";

  const navConfig = [
    {
      id: "playground",
      label: "Playground",
      to: "/api/playground/",
      active: activeTab === "playground",
    },
    {
      id: "usage",
      label: "Usage",
      to: "/api/usage/",
      active: activeTab === "usage",
    },
    {
      id: "keys",
      label: "Keys",
      to: "/api/keys/",
      active: activeTab === "keys",
    },
  ];

  const secondaryActions = [
    <a
      key="docs"
      href={`${getExternalApiBaseUrl()}/docs/`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 rounded-lg border border-theme-secondary px-3 py-2 text-sm font-medium text-theme-text hover:opacity-80"
    >
      <BookOpenIcon className="h-4 w-4" />
      Docs
    </a>,
  ];

  const primaryAction = (
    <button
      type="button"
      onClick={() => navigate(`/api/${activeTab}/agent-instructions/`)}
      className="inline-flex items-center gap-2 rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-theme-button"
      style={{ backgroundColor: "var(--theme-primary)", color: "var(--theme-button-text)" }}
    >
      <RobotHeadIcon className="h-4 w-4" />
      Agents
    </button>
  );

  return (
    <DashboardLayout
      title="API"
      currentUser={currentUser}
      navConfig={navConfig}
      secondaryActions={secondaryActions}
      primaryAction={primaryAction}
    >
      <Routes>
        <Route path="playground/*" element={<ApiPlayground />} />
        <Route
          path="usage/*"
          element={
            <ApiUsage
              onSelectRequest={(row) => navigate(`/api/usage/${row.hash}/`)}
            />
          }
        />
        <Route path="keys/*" element={<ApiKeys />} />
        <Route path="" element={<Navigate to="playground/" replace />} />
        <Route path="*" element={<Navigate to="playground/" replace />} />
      </Routes>

      <ApiRequestDetailWrapper />
      <ModalRoute
        path="/api/playground/agent-instructions"
        parentPath="/api/playground/"
        element={
          <AgentInstructionsModal onClose={() => navigate("/api/playground/")} />
        }
        maxWidth="max-w-2xl"
        heightClass="h-[90vh] max-h-[90vh]"
        noPadding
      />
      <ModalRoute
        path="/api/usage/agent-instructions"
        parentPath="/api/usage/"
        element={
          <AgentInstructionsModal onClose={() => navigate("/api/usage/")} />
        }
        maxWidth="max-w-2xl"
        heightClass="h-[90vh] max-h-[90vh]"
        noPadding
      />
      <ModalRoute
        path="/api/keys/agent-instructions"
        parentPath="/api/keys/"
        element={
          <AgentInstructionsModal onClose={() => navigate("/api/keys/")} />
        }
        maxWidth="max-w-2xl"
        heightClass="h-[90vh] max-h-[90vh]"
        noPadding
      />
    </DashboardLayout>
  );
};

function ApiRequestDetailWrapper() {
  const navigate = useNavigate();
  const match = useLocation().pathname.match(/\/api\/usage\/([^/]+)/);
  const hash = match?.[1];
  if (!hash || hash === "agent-instructions") return null;
  return <ApiRequestDetail hash={hash} onClose={() => navigate("/api/usage/")} />;
}

export default Api;
